import React, { memo } from 'react'
import { FieldErrors } from 'react-hook-form'
import ControlledAutocompleteShipper from 'transfix-form/components/ControlledAutocompleteShipper'
import ControlledCheckbox from 'transfix-form/components/ControlledCheckbox'
import { FormIf } from 'transfix-form/components/FormIf'
import Card from 'transfix-ui/components/Card'
import Grid from 'transfix-ui/core/Grid'
import { makeStyles } from 'transfix-ui/core/styles'
import { IEquipmentAttributeTypes, IEquipmentTypes } from 'types/graphqlTypes'
import { useCreateShipmentFormContext } from '../../context'
import { TAutocompleteValueSchema } from '../../schema/AutocompleteValueSchema'
import { getAutocompleteErrorMessage } from '../../utils/formErrorUtils'
import ControlledEquipmentSelect from './ControlledEquipmentSelect'
import DryVanFormSection from './DryVanFormSection'
import OpenDeckEquipmentFormSection from './OpenDeckEquipmentFormSection'
import ReeferEquipmentFormSection from './ReeferEquipmentFormSection'
import SpecializedEquipmentFormSection from './SpecializedEquipmentFormSection'

export interface IEquipmentDetailsFormSectionProps {}

const useEquipmentDetailsFormSectionStyles = makeStyles(() => ({
  reeferCheckboxWrapper: {
    alignSelf: 'flex-end',
  },
}))

const EquipmentDetailsFormSection = memo<IEquipmentDetailsFormSectionProps>(() => {
  const classes = useEquipmentDetailsFormSectionStyles()

  /**
   * Form Context
   */
  const { control, errors } = useCreateShipmentFormContext()

  /**
   * Errors
   */
  const { shipperApplication } = errors || {}
  const shipperApplicationError = shipperApplication as FieldErrors<TAutocompleteValueSchema>

  return (
    <Card>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <ControlledAutocompleteShipper
            control={control}
            name='shipperApplication'
            AutocompleteShipperProps={{
              label: 'Shipper',
              dataTest: 'shipper-application-field',
              inputError: !!getAutocompleteErrorMessage(shipperApplicationError, 'id'),
              inputValidationMessage: getAutocompleteErrorMessage(shipperApplicationError, 'id'),
              required: true,
              size: 'small',
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledEquipmentSelect />
        </Grid>
        <FormIf control={control} name='equipment' when={IEquipmentTypes.dry_van}>
          <Grid item xs={12} md={3} className={classes.reeferCheckboxWrapper}>
            <ControlledCheckbox
              control={control}
              name={`equipmentAttributes.${IEquipmentAttributeTypes.can_reefer}`}
              CheckboxProps={{
                label: 'Can load reefer',
                dataTest: 'can-reefer-field',
              }}
            />
          </Grid>
        </FormIf>
        <Grid item xs={12}>
          <FormIf control={control} name='equipment' when={IEquipmentTypes.dry_van}>
            <DryVanFormSection />
          </FormIf>
          <FormIf control={control} name='equipment' when={IEquipmentTypes.reefer}>
            <ReeferEquipmentFormSection />
          </FormIf>
          <FormIf control={control} name='equipment' when={IEquipmentTypes.open_deck}>
            <OpenDeckEquipmentFormSection />
          </FormIf>
          <FormIf control={control} name='equipment' when={IEquipmentTypes.specialized}>
            <SpecializedEquipmentFormSection />
          </FormIf>
        </Grid>
      </Grid>
    </Card>
  )
})

export default EquipmentDetailsFormSection
